import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useCart } from '@/context/CartContext';

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [status, setStatus] = useState(cancelled ? 'cancelled' : 'checking');
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled) return;
    const sessionId = params.get('session_id');
    if (!sessionId) { setStatus('error'); return; }
    const poll = async () => {
      if (attempts.current >= 8) { setStatus('timeout'); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') { setStatus('paid'); clear(); return; }
        if (data.status === 'expired') { setStatus('error'); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [cancelled]);

  const Wrap = ({ children }) => (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md">{children}</div>
    </div>
  );

  if (status === 'checking') return <Wrap><Loader2 size={48} className="animate-spin text-gold mx-auto mb-6" /><h1 className="font-serif text-3xl mb-3">Confirming your order</h1><p className="text-muted-foreground text-sm">Please hold while we verify your payment…</p></Wrap>;

  if (status === 'paid') return <Wrap>
    <CheckCircle2 size={56} className="text-gold mx-auto mb-6" data-testid="payment-success-icon" />
    <h1 className="font-serif text-4xl mb-3">Order Confirmed</h1>
    <p className="text-muted-foreground text-sm mb-8">Thank you. Your pieces are being prepared and your courier window is reserved. A confirmation has been recorded to your account.</p>
    <div className="flex gap-4 justify-center">
      <Link to="/account" className="bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">View Orders</Link>
      <Link to="/shop" className="border border-border px-6 py-3 text-xs font-mono uppercase tracking-widest hover:border-gold">Continue</Link>
    </div>
  </Wrap>;

  return <Wrap>
    <XCircle size={56} className="text-muted-foreground mx-auto mb-6" />
    <h1 className="font-serif text-4xl mb-3">{status === 'cancelled' ? 'Checkout Cancelled' : 'Payment Not Completed'}</h1>
    <p className="text-muted-foreground text-sm mb-8">{status === 'timeout' ? 'We could not confirm your payment yet. Check your account shortly.' : 'No charge was made. Your bag has been saved.'}</p>
    <Link to="/checkout" className="bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Return to Checkout</Link>
  </Wrap>;
}
